// client/src/writer/hooks/useProjectSetupLogic.ts
import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../../lib/queryClient';
import { useToast } from '../../hooks/use-toast';
import { deriveLiteraryFormFromBookType, getDefaultWordCountForBookType } from '../utils/bookTypeUtils';
import { cacheManager } from '../../utils/cacheUtils';
import { apiService } from '../../services/api';

import type { ProjectState } from '../types/project';
import type { Chapter, WorldBuildingData, LoadingStates } from '../types/core';
import type { BookMetadata } from '../types';
import { getProjectTitle, getTargetWordCount } from '../utils/projectUtils';
import { initialChapterVitals, initialAuthorDetails, initialFrontMatter, initialBackMatter, initialPublishingEssentials } from '../constants';
import type { CoreState } from './useCoreState';
import type { ModalControls } from './useModalStates';
import type { MainView } from '../types/core';

export interface ProjectSetupLogic {
    handleGenerateInitialBlueprint: () => Promise<void>;
    handleGenerateChapterOutlines: () => Promise<void>;
    fixChapterWordCounts: () => Promise<void>;
}

export const useProjectSetupLogic = (
    coreState: CoreState,
    modalControls: ModalControls,
    onViewChange?: (view: MainView) => void
): ProjectSetupLogic => {
    const {
        projectState, setProjectState, apiKey, setError,
        getCoreProjectContextForAI, isInitialBlueprintReady, setMainView, setChaptersReady,
        setLoadingState,
    } = coreState;
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const aiMutation = useMutation({
        mutationFn: async ({ task, payload }: { task: string, payload: any }) => {
            const response = await apiRequest(`/api/llm/${task}`, "POST", payload);
            return response.json();
        },
        onSuccess: async (data, variables) => {
            const { task, payload } = variables;
            let successMessage = "AI action successful!";
            
            if (task === 'initial-blueprint') {
                // NEW SEGREGATED STRUCTURE: Handle separate blueprint and bookMetadata objects
                const { blueprint, bookMetadata: newBookMetadata } = data.data;
                const worldBuildingSuggestions = blueprint?.worldBuildingSuggestions || data.data.worldBuildingSuggestions; // Fallback for legacy response

                const newWorldBuildingData: WorldBuildingData = {
                    storyTitleIdeas: worldBuildingSuggestions.storyTitleIdeas || [],
                    selectedStoryTitle: (worldBuildingSuggestions.storyTitleIdeas && worldBuildingSuggestions.storyTitleIdeas[0]) || '',
                    logLine: worldBuildingSuggestions.logLine || '',
                    storyStartConcept: worldBuildingSuggestions.storyStartConcept || '',
                    storyEndConcept: worldBuildingSuggestions.storyEndConcept || '',
                    characters: (worldBuildingSuggestions.characters || []).map((sc: any, i: number) => ({
                        id: `char-bp-${Date.now()}-${i}`,
                        name: sc.name,
                        briefDescription: sc.description || sc.briefDescription || ''
                    })),
                    locations: (worldBuildingSuggestions.locations || []).map((sl: any, i: number) => ({
                        id: `loc-bp-${Date.now()}-${i}`,
                        name: sl.name,
                        briefDescription: sl.description || sl.briefDescription || ''
                    })),
                    customCategories: (worldBuildingSuggestions.customCategories || []).map((cat: any, catIndex: number) => ({
                        id: `ccat-bp-${Date.now()}-${catIndex}`,
                        categoryName: cat.name || cat.categoryName,
                        items: (cat.entries || cat.items || []).map((item: any, itemIndex: number) => ({
                            id: `citem-bp-${Date.now()}-${catIndex}-${itemIndex}`,
                            name: item.name,
                            description: item.description,
                        })),
                    })),
                    themes: worldBuildingSuggestions.themes || [],
                };

                setProjectState(prev => {
                    if (!prev) return null;
                    return {
                        ...prev,
                        chapters: [],
                        bookMetadata: {
                            ...(prev.bookMetadata || {}),
                            ...newBookMetadata,
                            title: newBookMetadata?.title || (worldBuildingSuggestions.storyTitleIdeas && worldBuildingSuggestions.storyTitleIdeas[0]) || prev.bookMetadata?.title || ''
                        },
                        blueprint: {
                            bookMetadata: {
                                ...(prev.bookMetadata || {}),
                                ...newBookMetadata,
                                title: newBookMetadata?.title || (worldBuildingSuggestions.storyTitleIdeas && worldBuildingSuggestions.storyTitleIdeas[0]) || prev.bookMetadata?.title || ''
                            },
                            worldBuildingSuggestions: newWorldBuildingData
                        },
                        authorDetails: { ...initialAuthorDetails },
                        frontMatter: { ...initialFrontMatter },
                        backMatter: { ...initialBackMatter },
                        publishingEssentials: { ...initialPublishingEssentials },
                        activeChapterId: null,
                        isUnsaved: true,
                        hasInitialBlueprint: true,
                        selectedChapterIdsForMerge: [],
                        bookMetadataSnapshotForChapters: null,
                        blueprintSnapshotForChapters: null,
                        blueprintSnapshotForHolisticRegen: null,
                    };
                });
                coreState.setIsInitialBlueprintReady(true);
                
                // Invalidate queries to refresh project data and blueprint status
                if (projectState?.projectId) {
                    queryClient.invalidateQueries({ queryKey: ["story-project", projectState.projectId] });
                    queryClient.invalidateQueries({ queryKey: ["project-blueprint-exists", projectState.projectId] });
                    queryClient.invalidateQueries({ queryKey: ["/api/story-projects/summaries"] });
                }
                
                const defaultSaveName = (projectState?.outlineIdea?.substring(0, 30) || "Untitled") + " Blueprint";
                modalControls.openSaveAsModal(defaultSaveName);
                // Navigate to the Book Details view to show the generated blueprint content
                // Small delay to ensure all state updates have propagated
                setTimeout(() => {
                    if (onViewChange) {
                        onViewChange('bookDetails');
                    } else {
                        setMainView('bookDetails');
                    }
                }, 100);
                successMessage = "Initial blueprint generated. Other tabs are now activated!";
            }
            toast({ title: "Success", description: successMessage });
        },
        onError: (error: any, variables) => {
            setError(`AI Error: ${error.message}`);
            toast({ title: "AI Error", description: error.message, variant: "destructive" });
            if (error.rawResponse) modalControls.openDebugResponseModal(error.rawResponse);
        },
        onSettled: (data, error, variables) => {
            const { task } = variables;
            if (task === 'initial-blueprint') setLoadingState('initialBlueprintGen', false);
        }
    });

    const handleGenerateInitialBlueprint = useCallback(async () => {
        if (!projectState?.outlineIdea?.trim()) { setError("Please enter a book idea."); return; }

        setChaptersReady(false); // Set chaptersReady to false when generating initial blueprint
        setLoadingState('initialBlueprintGen', true); setError(null);
        aiMutation.mutate({
            task: 'initial-blueprint',
            payload: {
                outlineIdea: projectState?.outlineIdea || '',
                bookType: coreState?.dbProject?.bookType || 'NOVEL',
                targetWordCount: getTargetWordCount(coreState.dbProject),
            }
        });
    }, [projectState, setChaptersReady, setLoadingState, setError, modalControls, aiMutation, coreState, setMainView]);

    const handleGenerateChapterOutlines = useCallback(async () => {
        // Delegate to coreState implementation to avoid duplication
        coreState.handleGenerateChapterOutlines();
    }, [coreState]);

    const fixChapterWordCounts = useCallback(async () => {
        if (!projectState?.chapters || projectState.chapters.length === 0) {
            return;
        }

        // Get total word count from database project
        const totalTargetWords = getTargetWordCount(coreState.dbProject);

        // Calculate words per chapter
        let wordsPerChapter = 500; // Default fallback
        if (totalTargetWords > 0) {
            wordsPerChapter = Math.round(totalTargetWords / projectState.chapters.length);
        } else {
            // Use genre-based defaults
            const bookType = (coreState.dbProject?.bookType || projectState?.bookMetadata?.genreSubject || 'novel').toLowerCase();
            if (bookType === 'novel') {
                wordsPerChapter = Math.round(80000 / projectState.chapters.length);
            } else if (bookType === 'novella') {
                wordsPerChapter = Math.round(30000 / projectState.chapters.length);
            } else if (bookType === 'short story') {
                wordsPerChapter = Math.round(5000 / projectState.chapters.length);
            }
        }

        console.log('Fixing chapter word counts:', {
            totalTargetWords,
            chaptersLength: projectState.chapters.length,
            wordsPerChapter,
            bookType: coreState.dbProject?.bookType || projectState?.bookMetadata?.genreSubject
        });

        // Update chapters that have 0 or missing word count
        const needsUpdate = projectState.chapters.some(ch => !ch.expectedWordCount || ch.expectedWordCount === 0);
        
        if (needsUpdate) {
            const updatedChapters = projectState.chapters.map(chapter => ({
                ...chapter,
                expectedWordCount: chapter.expectedWordCount && chapter.expectedWordCount > 0 
                    ? chapter.expectedWordCount 
                    : wordsPerChapter
            }));
            
            // Update local state
            setProjectState(prev => {
                if (!prev) return null;
                return {
                    ...prev,
                    chapters: updatedChapters,
                    isUnsaved: false // Mark as saved since we're saving to database
                };
            });
            
            // Save to database immediately
            if (projectState.projectId) {
                try {
                    setLoadingState('save', true);
                    await apiRequest(`/api/story-projects/${projectState.projectId}`, 'PUT', {
                        projectData: { 
                            ...projectState, 
                            chapters: updatedChapters,
                            isUnsaved: false 
                        },
                        title: getProjectTitle(coreState.dbProject, projectState.outlineIdea.substring(0, 50)),
                        description: projectState.outlineIdea,
                        bookType: coreState.dbProject?.bookType || 'NOVEL',
                        targetWordCount: coreState.dbProject?.targetWordCount || getDefaultWordCountForBookType(coreState.dbProject?.bookType || 'NOVEL'),
                        status: 'active',
                    });
                    
                    toast({ 
                        title: "Word counts updated and saved", 
                        description: `Set expected word count to ${wordsPerChapter} words per chapter and saved to database.` 
                    });
                } catch (error: any) {
                    setError(`Save Error: ${error.message}`);
                    toast({ 
                        title: "Word counts updated but not saved", 
                        description: `Set expected word count to ${wordsPerChapter} words per chapter, but failed to save to database. Please use 'Save Project' manually.`,
                        variant: "destructive"
                    });
                    // Mark as unsaved if save failed
                    setProjectState(prev => prev ? { ...prev, isUnsaved: true } : null);
                } finally {
                    setLoadingState('save', false);
                }
            } else {
                toast({ 
                    title: "Word counts updated", 
                    description: `Set expected word count to ${wordsPerChapter} words per chapter. Remember to 'Save Project'.` 
                });
            }
        } else {
            toast({ 
                title: "No changes needed", 
                description: "All chapters already have valid word counts." 
            });
        }
    }, [projectState, setProjectState, setLoadingState, setError, toast]);

    return {
        handleGenerateInitialBlueprint,
        handleGenerateChapterOutlines,
        fixChapterWordCounts,
    };
};
